import React, { useEffect, useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import { Alert, Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { getCroLeadStatusNew } from '../../../redux/features/croSlice';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { changeStudentStatusBulk, getBatchAdmissionRegister } from '../../../redux/features/batchSlice';

// Bulk version of "Update Student Status": applies one status + remarks to all checked students.
const BulkStudentStatus = ({ open, setOpen, batchAssignmentIds = [], dispatchParams, onSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { croLeadStatusNew } = useSelector((state) => state.cro);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(getCroLeadStatusNew());
    }
  }, [open]);

  const formik = useFormik({
    initialValues: { cro_lead_status: '', cro_remarks: '' },
    validationSchema: Yup.object().shape({
      cro_lead_status: Yup.string().required('Select a student status'),
    }),
    onSubmit: (values) => {
      setSubmitting(true);
      dispatch(changeStudentStatusBulk({ batch_assignment_ids: batchAssignmentIds, ...values }))
        .then((res) => {
          if (res?.payload?.status === 'success') {
            enqueueSnackbar(res.payload.message, { variant: 'success' });
            dispatch(getBatchAdmissionRegister(dispatchParams));
            onSuccess?.();
            setOpen(false);
          } else {
            enqueueSnackbar(res?.payload?.message || 'Failed to update status', { variant: 'error' });
          }
        })
        .finally(() => setSubmitting(false));
    },
  });

  return (
    <GlobalDialog
      title={'Bulk Update Student Status'}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={'sm'}
      closeButton={true}
      content={
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Stack direction={'column'} spacing={2}>
              <Alert severity="info">{batchAssignmentIds.length} student(s) selected</Alert>
              <TextField
                select
                fullWidth
                label="Student Status"
                name="cro_lead_status"
                value={formik.values.cro_lead_status}
                onChange={formik.handleChange}
                error={formik.touched.cro_lead_status && Boolean(formik.errors.cro_lead_status)}
                helperText={formik.touched.cro_lead_status && formik.errors.cro_lead_status}
              >
                {croLeadStatusNew.map((status) => (
                  <MenuItem key={status.cro_lead_status_id} value={status.cro_lead_status_id}>
                    {status.cro_lead_status_name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Remarks"
                name="cro_remarks"
                multiline
                rows={2}
                value={formik.values.cro_remarks}
                onChange={formik.handleChange}
              />
            </Stack>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
              <Stack direction={'row'} spacing={1}>
                <Button size="small" color="secondary" variant="outlined" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  type="submit"
                  disabled={submitting || !batchAssignmentIds.length}
                >
                  {submitting ? 'Please wait...' : `Update ${batchAssignmentIds.length} Student(s)`}
                </Button>
              </Stack>
            </Box>
          </Form>
        </FormikProvider>
      }
    />
  );
};

export default BulkStudentStatus;
